import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If OAuth callback pending in URL fragment, skip the /me check.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const startEmailOtp = async (email, name) => {
    // Backend replies generically — never leaks account existence. Errors
    // (validation, rate limit) still surface so the UI can react.
    const r = await api.post("/auth/email/start", { email, name });
    return r.data;
  };

  const verifyEmailOtp = async (email, code) => {
    const r = await api.post("/auth/email/verify", { email, code });
    if (r.data.token) localStorage.setItem("oh_token", r.data.token);
    setUser(r.data.user);
    if (r.data.is_new_user) {
      try {
        const helpers = await import("./api");
        const src = helpers.getCampaignSource();
        const match = window.location.pathname.match(/\/message\/([^/]+)/);
        if (match && src) {
          helpers.trackEvent(match[1], "new_human", { source: src });
        }
      } catch (_) { /* silent */ }
    }
    return r.data.user;
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("oh_token");
    setUser(null);
  };

  const requireAuth = (action) => {
    if (user) {
      action?.();
      return true;
    }
    setPendingAction(() => action);
    setModalOpen(true);
    return false;
  };

  const closeModal = () => setModalOpen(false);

  const onAuthed = () => {
    setModalOpen(false);
    const cb = pendingAction;
    setPendingAction(null);
    setTimeout(() => cb?.(), 50);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, startEmailOtp, verifyEmailOtp, loginGoogle,
      logout, requireAuth, modalOpen, closeModal, onAuthed }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
